import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getBase64ImageFromUrl } from './pdfGenerator';

const NAVY = [24, 33, 109];
const GOLD = [255, 196, 37];
const LOGO_URL = '/kianda-school-logo-removebg-preview.png';

// ---- Excel cell styles (xlsx-js-style) ----
const BORDER = {
    top: { style: 'thin', color: { rgb: 'E2E8F0' } },
    bottom: { style: 'thin', color: { rgb: 'E2E8F0' } },
    left: { style: 'thin', color: { rgb: 'E2E8F0' } },
    right: { style: 'thin', color: { rgb: 'E2E8F0' } }
};
const STYLE_HEADER = {
    font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
    fill: { patternType: 'solid', fgColor: { rgb: '18216D' } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: BORDER
};
const STYLE_CELL = { border: BORDER, alignment: { vertical: 'center' } };
const STYLE_CENTER = { border: BORDER, alignment: { horizontal: 'center', vertical: 'center' } };
const STYLE_GOOD = {
    font: { bold: true, color: { rgb: '166534' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'DCFCE7' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: BORDER
};
const STYLE_BAD = {
    font: { bold: true, color: { rgb: '991B1B' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'FEE2E2' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: BORDER
};
const STYLE_NEUTRAL = {
    font: { color: { rgb: '64748B' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: BORDER
};

const polarityStyle = (value, positive) => {
    if (!value) return STYLE_NEUTRAL;
    return positive ? STYLE_GOOD : STYLE_BAD;
};
const netStyle = (value) => (value > 0 ? STYLE_GOOD : value < 0 ? STYLE_BAD : STYLE_NEUTRAL);

const setStyle = (ws, row, col, style) => {
    const ref = XLSX.utils.encode_cell({ r: row, c: col });
    if (ws[ref]) ws[ref].s = style;
};

/**
 * Style a table whose header is on `headerRow` (0-based) and whose data fills the rest of the sheet range.
 * `columnStyle(colName, cellValue, rowData)` may return a style for a data cell; otherwise a plain bordered cell.
 */
const styleTable = (ws, headerRow, columnStyle) => {
    const range = XLSX.utils.decode_range(ws['!ref']);
    const names = [];
    for (let c = range.s.c; c <= range.e.c; c += 1) {
        const cell = ws[XLSX.utils.encode_cell({ r: headerRow, c })];
        names[c] = cell ? String(cell.v) : '';
        setStyle(ws, headerRow, c, STYLE_HEADER);
    }
    for (let r = headerRow + 1; r <= range.e.r; r += 1) {
        for (let c = range.s.c; c <= range.e.c; c += 1) {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            if (!cell) continue;
            cell.s = (columnStyle && columnStyle(names[c], cell.v, r)) || STYLE_CELL;
        }
    }
    const rows = ws['!rows'] || [];
    rows[headerRow] = { hpt: 24 };
    ws['!rows'] = rows;
};

const formatRange = (report) =>
    report.date_from === report.date_to ? report.date_from : `${report.date_from} to ${report.date_to}`;

const groupWord = (report) => (report.group_by === 'house' ? 'House' : 'Class');

const topBy = (items, key, dir = 'max') => {
    if (!items.length) return null;
    return items.reduce((best, cur) => {
        if (dir === 'max' ? cur[key] > best[key] : cur[key] < best[key]) return cur;
        return best;
    });
};

/**
 * Plain-language comparison lines for the bottom of the report,
 * e.g. "House Kenya leads on merits with 42 points (next: Tanzania with 31)".
 */
export const buildClosingSummary = (report) => {
    const word = groupWord(report);
    const lines = [];
    const groups = report.groups || [];

    const merited = [...groups].sort((a, b) => b.merits - a.merits);
    if (merited.length && merited[0].merits > 0) {
        const next = merited[1] ? ` (next: ${merited[1].name} with ${merited[1].merits})` : '';
        lines.push(`${word} with the most merit points: ${merited[0].name} with ${merited[0].merits} points${next}.`);
    }
    const demerited = [...groups].sort((a, b) => b.demerits - a.demerits);
    if (demerited.length && demerited[0].demerits > 0) {
        lines.push(`${word} with the most demerit points: ${demerited[0].name} with ${demerited[0].demerits} points.`);
    }
    const net = topBy(groups, 'net');
    if (net && groups.length > 1) {
        lines.push(`Best net standing: ${net.name} (${net.net >= 0 ? '+' : ''}${net.net} net points).`);
    }

    const topMerit = topBy(report.students.filter(s => s.merits > 0), 'merits');
    if (topMerit) lines.push(`Most merited scholar: ${topMerit.name} (${topMerit.class}) with ${topMerit.merits} merit points.`);
    const topDemerit = topBy(report.students.filter(s => s.demerits > 0), 'demerits');
    if (topDemerit) lines.push(`Most demerited scholar: ${topDemerit.name} (${topDemerit.class}) with ${topDemerit.demerits} demerit points.`);

    const topCategory = (report.categories || [])[0];
    if (topCategory) {
        lines.push(`Most frequently logged category: ${topCategory.name} (${topCategory.count} incidents, ${topCategory.points} points).`);
    }

    const totalIncidents = groups.reduce((n, g) => n + g.incident_count, 0);
    lines.push(`Total incidents logged in this period: ${totalIncidents}.`);
    return lines;
};

const summaryRows = (report) =>
    report.students.map(s => ({
        Student: s.name,
        Adm: s.adm,
        Class: s.class,
        House: s.house,
        Merits: s.merits,
        Demerits: s.demerits,
        Net: s.net,
        Status: s.status
    }));

const incidentRows = (report) =>
    report.students.flatMap(s =>
        s.incidents.map(i => ({
            Student: s.name,
            Adm: s.adm,
            Class: s.class,
            House: s.house,
            Date: i.date,
            Category: i.category,
            Type: i.type === 'merit' ? 'Merit' : 'Demerit',
            Points: i.type === 'merit' ? i.points : -i.points,
            Severity: i.severity,
            Description: i.description,
            'Logged By': i.logged_by
        }))
    );

const autoWidth = (rows) => {
    if (!rows.length) return [];
    return Object.keys(rows[0]).map(k => ({
        wch: Math.min(50, Math.max(k.length, ...rows.map(r => String(r[k] ?? '').length)) + 2)
    }));
};

const fileStem = (report, targetLabel) => {
    const safe = (targetLabel || 'All').replace(/[^a-z0-9]+/gi, '_');
    return `Character_Report_${groupWord(report)}_${safe}_${report.date_from}_to_${report.date_to}`;
};

export const exportDisciplineReportExcel = (report, targetLabel) => {
    const wb = XLSX.utils.book_new();

    const summary = summaryRows(report);
    const wsSummary = XLSX.utils.json_to_sheet(summary);
    wsSummary['!cols'] = autoWidth(summary);
    styleTable(wsSummary, 0, (name, value) => {
        if (name === 'Merits') return polarityStyle(value, true);
        if (name === 'Demerits') return polarityStyle(value, false);
        if (name === 'Net') return netStyle(value);
        if (name === 'Adm') return STYLE_CENTER;
        return null;
    });
    wsSummary['!freeze'] = { xSplit: 0, ySplit: 1 };
    wsSummary['!views'] = [{ state: 'frozen', ySplit: 1 }];
    wsSummary['!autofilter'] = { ref: wsSummary['!ref'] };
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Student Summary');

    const incidents = incidentRows(report);
    const wsIncidents = XLSX.utils.json_to_sheet(incidents.length ? incidents : [{ Student: 'No incidents logged in this period' }]);
    wsIncidents['!cols'] = autoWidth(incidents);
    styleTable(wsIncidents, 0, (name, value, r) => {
        if (name === 'Points') return value > 0 ? STYLE_GOOD : STYLE_BAD;
        if (name === 'Type') return incidents[r - 1]?.Type === 'Merit' ? STYLE_GOOD : STYLE_BAD;
        if (name === 'Date' || name === 'Adm' || name === 'Severity') return STYLE_CENTER;
        return null;
    });
    wsIncidents['!views'] = [{ state: 'frozen', ySplit: 1 }];
    wsIncidents['!autofilter'] = { ref: wsIncidents['!ref'] };
    XLSX.utils.book_append_sheet(wb, wsIncidents, 'Incident Log');

    // Summary sheet: title block, comparison table, closing lines
    const compareHeaderRow = 5;
    const aoa = [
        [`Character Report - ${groupWord(report)}: ${targetLabel || 'All'}`],
        [`Period: ${formatRange(report)}`],
        [`Generated by ${report.generated_by} on ${report.generated_at}`],
        [],
        [`${groupWord(report)} comparison`],
        [groupWord(report), 'Scholars', 'Merits', 'Demerits', 'Net', 'Incidents'],
        ...report.groups.map(g => [g.name, g.students, g.merits, g.demerits, g.net, g.incident_count]),
        [],
        ['Summary'],
        ...buildClosingSummary(report).map(l => [l])
    ];
    const wsOverview = XLSX.utils.aoa_to_sheet(aoa);
    wsOverview['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }];
    setStyle(wsOverview, 0, 0, { font: { bold: true, sz: 14, color: { rgb: '18216D' } } });
    setStyle(wsOverview, 4, 0, { font: { bold: true, color: { rgb: '18216D' } } });
    const lastCompareRow = compareHeaderRow + report.groups.length;
    for (let c = 0; c <= 5; c += 1) setStyle(wsOverview, compareHeaderRow, c, STYLE_HEADER);
    for (let r = compareHeaderRow + 1; r <= lastCompareRow; r += 1) {
        setStyle(wsOverview, r, 0, STYLE_CELL);
        setStyle(wsOverview, r, 1, STYLE_CENTER);
        setStyle(wsOverview, r, 2, polarityStyle(report.groups[r - compareHeaderRow - 1].merits, true));
        setStyle(wsOverview, r, 3, polarityStyle(report.groups[r - compareHeaderRow - 1].demerits, false));
        setStyle(wsOverview, r, 4, netStyle(report.groups[r - compareHeaderRow - 1].net));
        setStyle(wsOverview, r, 5, STYLE_CENTER);
    }
    setStyle(wsOverview, lastCompareRow + 2, 0, { font: { bold: true, color: { rgb: '18216D' } } });
    XLSX.utils.book_append_sheet(wb, wsOverview, 'Summary');

    XLSX.writeFile(wb, `${fileStem(report, targetLabel)}.xlsx`);
};

// PDF carries the student summary table and closing comparison only (no incident detail).
export const exportDisciplineReportPDF = async (report, targetLabel) => {
    const logo = await getBase64ImageFromUrl(LOGO_URL);
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;

    // Left: logo and title
    let titleX = margin;
    if (logo) {
        doc.addImage(logo, 'PNG', margin, 8, 20, 20, undefined, 'FAST');
        titleX = margin + 25;
    }
    doc.setFont(undefined, 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...NAVY);
    doc.text('Character Report', titleX, 21);

    // Right corner: class/house, period, generation time (no user name)
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60);
    doc.text(`${groupWord(report)}: ${targetLabel || 'All'}`, pageWidth - margin, 13, { align: 'right' });
    doc.text(`Period: ${formatRange(report)}`, pageWidth - margin, 19, { align: 'right' });
    doc.text(`Generated: ${report.generated_at}`, pageWidth - margin, 25, { align: 'right' });

    // Navy rule with gold accent
    doc.setFillColor(...NAVY);
    doc.rect(margin, 31, pageWidth - margin * 2, 0.8, 'F');
    doc.setFillColor(...GOLD);
    doc.rect(margin, 31, 40, 0.8, 'F');

    autoTable(doc, {
        startY: 37,
        head: [['Student', 'Adm', 'Class', 'House', 'Merits', 'Demerits', 'Net', 'Status']],
        body: report.students.map(s => [s.name, s.adm, s.class, s.house, s.merits, s.demerits, s.net, s.status]),
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9 },
        styles: { fontSize: 9 },
        columnStyles: { 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } },
        alternateRowStyles: { fillColor: [245, 247, 255] }
    });

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [[`${groupWord(report)} comparison`, 'Scholars', 'Merits', 'Demerits', 'Net', 'Incidents']],
        body: report.groups.map(g => [g.name, g.students, g.merits, g.demerits, g.net, g.incident_count]),
        headStyles: { fillColor: NAVY, textColor: 255, fontSize: 9 },
        styles: { fontSize: 9 },
        tableWidth: 160
    });

    let y = doc.lastAutoTable.finalY + 10;
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFont(undefined, 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...NAVY);
    if (y > pageHeight - 40) { doc.addPage(); y = 18; }
    doc.text('Summary', margin, y);
    y += 7;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(40);
    buildClosingSummary(report).forEach(line => {
        const wrapped = doc.splitTextToSize(`- ${line}`, pageWidth - margin * 2);
        if (y + wrapped.length * 5 > pageHeight - 12) { doc.addPage(); y = 18; }
        doc.text(wrapped, margin, y);
        y += wrapped.length * 5 + 1;
    });

    doc.save(`${fileStem(report, targetLabel)}.pdf`);
};
